import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AvTooltip, InputPopper } from '@fabric-ui/components';
import { Alert, Box, Divider, FormControl, IconButton, LinearProgress } from '@mui/material';
import 'react-quill-new/dist/quill.snow.css';
import {
  ArrowDown,
  Attach,
  Blockquote,
  Bold,
  Bulletlist,
  Code,
  CodeBlock,
  Image,
  Italic,
  Link as LinkSVG,
  Numberslist,
  Strikethrough,
  Underline,
} from '@fabric-ui/icons';
import { FailFull as ErrorIcon } from '@fabric-ui/icons/colorful';
import { flex, shadowPrimaryBottomMd } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import ReactQuill, { Quill } from 'react-quill-new';
import { AttachmentList } from '../../components/AvPreviewImage';
import { generateID } from '../../utils/utils';

const formats: string[] = ['bold', 'italic', 'size', 'underline', 'strike', 'blockquote', 'list', 'link', 'image', 'code', 'code-block'];
const icons = Quill.import('ui/icons') as Record<string, any>;
formats.forEach(format => {
  icons[format] = undefined;
});

const Block = Quill.import('blots/block') as any;
Block.tagName = 'div';
Quill.register(Block, true);

const fontSizeArr: string[] = [
  '8px',
  '9px',
  '10px',
  '12px',
  '14px',
  '16px',
  '20px',
  '24px',
  '32px',
  '42px',
  '54px',
  '68px',
  '84px',
  '98px',
];
const Size = Quill.import('attributors/style/size') as { whitelist: string[] };
Size.whitelist = fontSizeArr;
Quill.register(Size as any, true);

interface ItemButtonProps {
  qlClass?: string;
  qlValue?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  title: string;
  icon: React.JSX.Element;
}

function ItemButton({ qlClass, qlValue, onClick, title, icon }: ItemButtonProps) {
  return (
    <AvTooltip muiProps={{ placement: 'top' }} title={title}>
      <IconButton className={qlClass} value={qlValue} onClick={onClick}>
        {icon}
      </IconButton>
    </AvTooltip>
  );
}

export interface AvRichTextProps {
  showBar?: boolean;
  value: string;
  onChange: (value: string) => void;
  fileUploadProps: {
    enable: boolean;
    attachmentsList: any[];
    handleAttachmentClick?: () => void;
    handleFileChange?: (files: FileList) => void;
    onDeleteFile?: (file: any) => void;
    downloadFile?: (file: any) => void;
    isLoading: boolean;
    projectionId?: any;
  };
  [key: string]: any;
}

const AvRichText: React.FC<AvRichTextProps> = ({
  showBar = true,
  value,
  onChange,
  fileUploadProps = {
    enable: true,
    attachmentsList: undefined,
    handleAttachmentClick: undefined,
    handleFileChange: undefined,
    onDeleteFile: undefined,
    downloadFile: undefined,
    isLoading: false,
    projectionId: undefined,
  },
  ...otherProps
}) => {
  const [popper, setPopper] = useState<{
    anchor: HTMLElement;
    text: string;
    onApply: (args: { input: string }) => void;
  } | null>(null);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const toolbarID = useRef(`toolbar_${generateID()}`).current;
  const quillRef = useRef<ReactQuill | null>(null);
  const getEditor = () => quillRef.current?.getEditor();

  const enabledFormats = useMemo(() => (fileUploadProps.enable ? formats : formats.filter(f => f !== 'image')), [fileUploadProps.enable]);

  const showError = useCallback(() => {
    setShowErrorMessage(true);
    setTimeout(() => {
      setShowErrorMessage(false);
    }, 2000);
  }, []);

  const popperProps = {
    image: {
      text: 'Image URL',
      onApply: ({ input: url }: { input: string }) => {
        const editor = getEditor();
        // @ts-ignore
        editor?.insertEmbed(editor.getSelection(), 'image', url);
      },
    },
  };

  const openPopper =
    (type: 'image') =>
    ({ currentTarget }: React.MouseEvent<HTMLElement>) =>
      setPopper({ anchor: currentTarget, ...popperProps[type] });

  const divider = <Divider flexItem orientation="vertical" sx={{ borderColor: 'var(--color-border-base-secondary)' }} />;
  const customToolbar = (
    <Box id={toolbarID}>
      <Box sx={{ position: 'relative', whiteSpace: 'nowrap' }}>
        <select className="ql-size" defaultValue="14px">
          {fontSizeArr.map(v => (
            <option value={v} key={v}>
              {v.split('px')[0]}
            </option>
          ))}
        </select>
        <ArrowDown
          style={{
            ...iconSize(14),
            position: 'absolute',
            right: '8px',
            top: '6px',
            color: 'var(--color-content-base-secondary)',
            zIndex: 2,
          }}
        />
      </Box>
      {divider}
      <ItemButton title="Bold" qlClass="ql-bold" icon={<Bold />} />
      <ItemButton title="Italic" qlClass="ql-italic" icon={<Italic />} />
      <ItemButton title="Underline" qlClass="ql-underline" icon={<Underline />} />
      <ItemButton title="Strikethrough" qlClass="ql-strike" icon={<Strikethrough />} />
      {divider}
      <ItemButton title="Numbered List" qlClass="ql-list" qlValue="ordered" icon={<Numberslist />} />
      <ItemButton title="Bulleted List" qlClass="ql-list" qlValue="bullet" icon={<Bulletlist />} />
      {divider}
      <ItemButton title="Blockquote" qlClass="ql-blockquote" icon={<Blockquote />} />
      <ItemButton title="Inline Code" qlClass="ql-code" icon={<Code />} />
      <ItemButton title="Code Block" qlClass="ql-code-block" icon={<CodeBlock />} />
      {divider}
      <ItemButton title="Link" qlClass="ql-link" icon={<LinkSVG />} />
      {fileUploadProps.enable && (
        <>
          <ItemButton title="Inset Image" onClick={openPopper('image')} icon={<Image />} />
          {divider}
          <ItemButton title="Add Attachment" onClick={fileUploadProps.handleAttachmentClick} icon={<Attach />} />
        </>
      )}
      {popper && (
        <InputPopper
          anchorEl={popper.anchor}
          onApply={popper.onApply}
          texts={{ input: popper.text, button: 'Apply' }}
          onClose={() => setPopper(null)}
        />
      )}
    </Box>
  );

  const forceTriggerChange = () => quillRef.current?.getEditor().getSelection();

  const eventHandler = (fileEvent: DataTransfer | null) => {
    const files = Array.from(fileEvent?.files || []);
    if (files.length > 0) {
      if (!fileUploadProps.enable) {
        showError();
        return;
      }
      if (fileEvent?.files) {
        fileUploadProps.handleFileChange?.(fileEvent.files);
      }
    }
  };

  useEffect(() => {
    const editorRoot = quillRef.current!.getEditor()!.root!;

    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault();
      eventHandler(event.clipboardData);
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      eventHandler(event.dataTransfer);
    };

    const handleDragOver = (event: DragEvent) => event.preventDefault();

    editorRoot.addEventListener('paste', handlePaste);
    editorRoot.addEventListener('drop', handleDrop);
    editorRoot.addEventListener('dragover', handleDragOver);

    return () => {
      editorRoot.removeEventListener('paste', handlePaste);
      editorRoot.removeEventListener('drop', handleDrop);
      editorRoot.removeEventListener('dragover', handleDragOver);
    };
  }, [fileUploadProps.enable, fileUploadProps.handleFileChange, showError]);

  return (
    <FormControl sx={wrapperStyle}>
      {showBar && customToolbar}
      <ReactQuill
        key={`${Boolean(showBar)}`}
        ref={quillRef}
        value={value}
        onKeyUp={forceTriggerChange}
        onChange={onChange}
        modules={{ toolbar: showBar && { container: `#${toolbarID}` }, clipboard: { matchVisual: false } }}
        formats={enabledFormats}
        preserveWhitespace
        {...otherProps}
        onKeyDown={(e: React.KeyboardEvent) => {
          e.stopPropagation();
          otherProps.onKeyDown?.(e);
        }}
      />
      {fileUploadProps.enable && !!fileUploadProps?.attachmentsList?.length && (
        <AttachmentList
          attachments={fileUploadProps.attachmentsList}
          onDeleteFile={fileUploadProps.onDeleteFile}
          deleteByIndex
          style={{ pl: 3, pt: 2, pb: 3 }}
          isLoading={fileUploadProps.isLoading}
          projection={fileUploadProps.projectionId}
        />
      )}
      {fileUploadProps.isLoading && <LinearProgress sx={{ height: '1px' }} />}
      {showErrorMessage && (
        <Alert
          sx={{
            position: 'absolute',
            top: '60%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            py: 0,
            '.MuiAlert-icon': { p: '8px 0px' },
          }}
          severity="error"
          icon={<ErrorIcon />}>
          File uploads are disabled
        </Alert>
      )}
    </FormControl>
  );
};

const wrapperStyle = ({ palette }: { palette: any }) => ({
  width: '100%',
  position: 'relative',
  ':before': {
    content: '""',
    height: '100%',
    width: '100%',
    pointerevents: 'none',
    position: 'absolute',
    borderRadius: '12px',
    transition: theme =>
      theme.transitions.create(['border-color'], {
        duration: theme.transitions.duration.shorter,
      }),
    border: `1px solid var(--color-border-base-secondary)`,
  },
  '&:hover:before': {
    border: `1px solid var(--color-border-interactive-secondary-hover)`,
  },
  '&:has(.ql-editor:focus):before': {
    border: `1px solid var(--color-border-interactive-primary-default)`,
  },
  '.ql-snow': {
    '&.ql-container': {
      border: 'none',
      fontSize: 'inherit',
      fontFamily: 'inherit',
      '.ql-editor.ql-blank:before': {
        fontStyle: 'inherit',
        color: 'var(--color-content-base-tertiary)',
      },
    },
    '&.ql-toolbar, .ql-toolbar': {
      ...flex.row,
      gap: 2,
      px: 2,
      py: '10px',
      backgroundColor: 'var(--color-surface-base-tertiary)',
      border: 'none',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      button: {
        display: 'inherit',
        // eslint-disable-next-line fabric-ui/css-best-practices
        float: 'none',
        height: 'inherit',
        padding: 0,
        width: 'inherit',
        '&, &:focus': {
          color: 'var(--color-content-base-secondary)',
        },
        '&.ql-active, &:hover': {
          color: 'var(--color-content-base-primary)',
        },
        '&:hover:before': {
          backgroundColor: 'var(--color-surface-interactive-secondary-hover)',
        },
        '&.ql-active:before': {
          backgroundColor: 'var(--color-surface-interactive-secondary-active)',
        },
      },
      ':after': {
        content: 'none',
      },
    },
    '.ql-tooltip': {
      left: '16px !important',
      px: 2,
      py: 1,
      color: 'var(--color-content-base-secondary)',
      borderRadius: '2px',
      border: 0,
      backgroundColor: 'var(--color-surface-elevated-10)',
      boxShadow: theme => theme.components.MuiPaper.styleOverrides.root.boxShadow,
      'input[type=text]': {
        borderRadius: '8px',
        px: 1,
        outline: 0,
        backgroundColor: 'var(--color-surface-field-default)',
        borderColor: 'var(--color-border-interactive-secondary-default)',
        transition: theme =>
          theme.transitions.create(['border-color', 'background-color'], {
            duration: theme.transitions.duration.shorter,
          }),
        ':hover': {
          backgroundColor: 'var(--color-surface-field-hover)',
          borderColor: 'var(--color-border-interactive-secondary-hover)',
        },
        ':focus': {
          borderColor: palette.primary.main,
        },
      },
      '.ql-action, .ql-remove': {
        color: 'inherit',
        fontWeight: 600,
        padding: '5px 10px',
        verticalAlign: 'middle',
        fontSize: 13,
        borderRadius: '8px',
        border: `1px solid var(--color-border-severity-none-secondary)`,
        ml: 1,
        ':before, :after': {
          p: 0,
          m: 0,
          border: 0,
        },
      },
      '&.ql-editing .ql-action': {
        backgroundColor: palette.primary.main,
        color: palette.white.main,
        border: 0,
      },
    },
    '.ql-editor .ql-code-block-container, .ql-editor code, .ql-editor pre.ql-syntax': {
      fontFamily: 'inherit',
      backgroundColor: 'var(--color-surface-base-tertiary)',
      color: 'inherit',
    },
    code: {
      fontSize: 13,
      padding: '2px 4px',
    },
    '.ql-editor a, a.ql-preview': {
      color: palette.primary.main,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '&.ql-toolbar.ql-snow .ql-picker-label': {
      border: `1px solid var(--color-border-interactive-secondary-default)`,
      borderRadius: '8px',
      padding: '0px 24px 0px 8px',
      backgroundColor: 'var(--color-surface-field-default)',
      color: 'var(--color-content-base-secondary)',
      ':hover': {
        color: 'var(--color-content-base-primary)',
        border: `1px solid var(--color-border-interactive-secondary-hover)`,
      },
    },
    '.ql-picker.ql-expanded .ql-picker-options': {
      border: 'none',
      backgroundColor: 'var(--color-surface-elevated-10)',
      boxShadow: shadowPrimaryBottomMd,
    },
    '&.ql-snow.ql-toolbar .ql-picker-item': {
      pl: '4px',
      color: 'var(--color-content-base-primary)',
      '&:hover': {
        backgroundColor: 'var(--color-surface-list-hover)',
      },
    },
    '.ql-picker': {
      svg: {
        display: 'none',
      },
      '&.ql-size': {
        width: 'unset',
      },
    },
    '.ql-editor blockquote': {
      borderColor: 'var(--color-surface-interactive-secondary-hover)',
    },
  },
});

export default AvRichText;
